import type {
  ConversationRef,
  ConversationSummary,
  CreateConversationOptions,
  RemoteMessage,
  SendMessageOptions,
  Skill,
  SkillGroup,
  Turn,
} from '../types';
import {
  AGENT_VERSION,
  AgentClient,
  canonicalPagePathname,
} from './AgentClient';
import { AgentTurn } from './AgentTurn';
import { AttachmentUploader } from './AttachmentUploader';
import { HttpError, HttpTransport } from './HttpTransport';

/** The same-origin proxy the add-on's express middleware provides. */
export const PROXY = '/_ai-agent';

interface RemoteConversation {
  conversation_id: string;
  title: string;
  page_link: string;
  language: 'de' | 'en';
  created_at: string;
  updated_at: string;
  message_count: number;
  preview: string;
  running?: boolean;
}

/**
 * The real agent, spoken to through the express proxy.
 *
 * The browser never learns the agent's URL or its API key: both live on the
 * Volto server, and this client only ever talks to its own origin.
 */
export class HttpAgentClient extends AgentClient {
  constructor(
    protected readonly transport: HttpTransport = new HttpTransport(PROXY),
    protected readonly uploader: AttachmentUploader = new AttachmentUploader(),
  ) {
    super();
  }

  protected toSummary(conversation: RemoteConversation): ConversationSummary {
    return {
      id: conversation.conversation_id,
      title: conversation.title,
      pathname: canonicalPagePathname(conversation.page_link),
      language: conversation.language,
      createdAt: conversation.created_at,
      updatedAt: conversation.updated_at,
      messageCount: conversation.message_count,
      preview: conversation.preview,
      running: !!conversation.running,
    };
  }

  protected async createRemote({
    state,
    pathname,
    language,
  }: CreateConversationOptions): Promise<ConversationRef> {
    const data = await this.transport.request<RemoteConversation>(
      '/conversations',
      {
        method: 'POST',
        body: {
          schema: 'volto',
          version: AGENT_VERSION,
          state: this.pagePayload(state, pathname),
          language: language === 'de' ? 'de' : 'en',
        },
      },
    );
    this.current = {
      conversationId: data.conversation_id,
      pathname: canonicalPagePathname(pathname),
      language,
    };

    return this.current;
  }

  async createConversation(
    options: CreateConversationOptions,
  ): Promise<{ conversationId: string }> {
    const conversation = await this.createRemote(options);

    return { conversationId: conversation.conversationId };
  }

  async listConversations({ pathname }: { pathname?: string } = {}): Promise<
    ConversationSummary[]
  > {
    const query = new URLSearchParams({ version: AGENT_VERSION });
    if (pathname !== undefined) {
      query.set('link', canonicalPagePathname(pathname));
    }
    const data = await this.transport.request<RemoteConversation[]>(
      `/conversations?${query.toString()}`,
    );

    return data.map((conversation) => this.toSummary(conversation));
  }

  async listSkills(): Promise<Skill[]> {
    const data = await this.transport.request<{ skills: Skill[] }>('/skills');

    return data.skills || [];
  }

  async listSkillGroups(): Promise<SkillGroup[]> {
    const data = await this.transport.request<{ groups: SkillGroup[] }>(
      '/skill-groups',
    );

    return data.groups || [];
  }

  getConversationMessages(conversationId: string): Promise<RemoteMessage[]> {
    return this.transport.request<RemoteMessage[]>(
      `/conversations/${conversationId}/messages`,
    );
  }

  async renameConversation(
    conversationId: string,
    title: string,
  ): Promise<ConversationSummary> {
    const conversation = await this.transport.request<RemoteConversation>(
      `/conversations/${conversationId}`,
      { method: 'PATCH', body: { title } },
    );

    return this.toSummary(conversation);
  }

  deleteConversation(conversationId: string): Promise<unknown> {
    return this.transport.request(`/conversations/${conversationId}`, {
      method: 'DELETE',
    });
  }

  /** The body of one turn. Override to send a field the stock agent ignores. */
  protected messageBody(
    options: SendMessageOptions,
    pathname: string,
    attachments: unknown[],
  ): Record<string, unknown> {
    const body: Record<string, unknown> = {
      message: options.message,
      state: this.pagePayload(options.state, pathname),
      permission: options.permission === 'write' ? 'write' : 'read',
      attachments,
      references: (options.references || []).map((reference) => ({
        link: reference['@id'] || reference.link || '',
        title: reference.title || null,
        type: reference.type || null,
      })),
      skills: (options.skills || []).map((skill) => skill.name),
    };
    const context = options.context;
    if (context && (context.block_id || context.text)) {
      body.context = context;
    }

    return body;
  }

  protected newTurn(conversationId: string, options: SendMessageOptions) {
    return new AgentTurn(this.transport, conversationId, options);
  }

  sendMessage(options: SendMessageOptions): Turn {
    if (!this.current) {
      options.onDone({
        newState: null,
        denied: false,
        error: { kind: 'noConversation' },
      });

      return { cancel: () => {} };
    }

    const pathname = this.current.pathname;
    const turn = this.newTurn(this.current.conversationId, options);

    const start = async () => {
      const attachments = await this.uploader.uploadAll(
        options.attachments || [],
        pathname,
      );
      if (turn.abandoned) return;

      const body = this.messageBody(options, pathname, attachments);
      let posted: { job_id: string; message_uid: string };
      try {
        posted = await this.post(body, options, pathname);
      } catch (error) {
        turn.fail(
          (error as HttpError).kind || 'unreachable',
          (error as Error).message,
        );

        return;
      }
      if (turn.abandoned) {
        // Cancel arrived while the POST was in flight — kill the job now that
        // its id is known.
        turn.adoptJob(posted.job_id);

        return;
      }
      await turn.run(posted.job_id, posted.message_uid);
    };

    start();

    return turn;
  }

  /**
   * Post the turn, transparently rebuilding the conversation when the agent
   * server was restarted underneath us and our id no longer exists there.
   */
  protected async post(
    body: Record<string, unknown>,
    options: SendMessageOptions,
    pathname: string,
  ): Promise<{ job_id: string; message_uid: string }> {
    const send = () =>
      this.transport.request<{ job_id: string; message_uid: string }>(
        `/conversations/${this.current!.conversationId}/messages`,
        { method: 'POST', body },
      );
    try {
      return await send();
    } catch (error) {
      if ((error as HttpError).status !== 404) throw error;
      await this.createRemote({
        state: options.state,
        pathname,
        language: this.current!.language,
      });

      return send();
    }
  }
}
